import { ApiError } from "api/client";
import { getSubscriptionStatus } from "api/subscriptions";
import { changeTreeVisibility } from "api/trees";
import type { TreeDto } from "api/types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "Session";

import ViewIcon from "./ViewIcon";

import styles from "./PublishMenu.module.css";

type PublishMenuProps = {
	/** Receives the updated tree after the visibility change succeeds. */
	onTreeUpdated: (tree: TreeDto) => void;
	treeId: string;
};

/**
 * Draft-only publish control: a button that opens a dropdown to convert the tree
 * to Public or Private. Private stays locked behind Pro, mirroring the edit dialog.
 * Admin-only (gated by caller).
 */
const PublishMenu = ({ onTreeUpdated, treeId }: PublishMenuProps) => {
	const { t } = useTranslation("homePage");
	const { getAccessToken } = useSession();
	const [isOpen, setIsOpen] = useState(false);
	const [canCreatePrivateTrees, setCanCreatePrivateTrees] = useState(false);
	const [isChanging, setIsChanging] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const wrapperRef = useRef<HTMLDivElement>(null);

	// Close when clicking anywhere outside the button or its dropdown.
	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}

		const onDocumentClick = (event: MouseEvent): void => {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		document.addEventListener("mousedown", onDocumentClick);

		return () => document.removeEventListener("mousedown", onDocumentClick);
	}, [isOpen]);

	const toggleOpen = useCallback(async (): Promise<void> => {
		setErrorMessage("");
		setIsOpen((open: boolean) => !open);

		// Pro entitlement gates the Private option; failure just leaves it locked.
		const token = await getAccessToken();
		if (!token) {
			return;
		}

		try {
			const status = await getSubscriptionStatus(token);
			setCanCreatePrivateTrees(status.canCreatePrivateTrees);
		} catch {
			// Ignore: keep Private gated by default.
		}
	}, [getAccessToken]);

	const changeVisibility = useCallback(
		async (visibility: string): Promise<void> => {
			if (isChanging) {
				return;
			}

			const token = await getAccessToken();
			if (!token) {
				return;
			}

			setErrorMessage("");
			setIsChanging(true);
			try {
				onTreeUpdated(await changeTreeVisibility(treeId, visibility, token));
				setIsOpen(false);
			} catch (error) {
				setErrorMessage(error instanceof ApiError ? error.message : t("view.publishError"));
			} finally {
				setIsChanging(false);
			}
		},
		[getAccessToken, isChanging, onTreeUpdated, t, treeId]
	);

	const onMakePublic = useCallback((): void => {
		changeVisibility("Public").catch(() => undefined);
	}, [changeVisibility]);
	const onMakePrivate = useCallback((): void => {
		changeVisibility("Private").catch(() => undefined);
	}, [changeVisibility]);

	return (
		<div ref={wrapperRef} className={styles.wrapper}>
			<button
				aria-expanded={isOpen}
				aria-haspopup="menu"
				className={styles.publishButton}
				onClick={toggleOpen}
				type="button">
				<ViewIcon name="globe" />
				<span>{t("view.publish")}</span>
				<ViewIcon name="chevronDown" />
			</button>

			{isOpen && (
				<div className={styles.dropdown} role="menu">
					<button
						className={styles.menuItem}
						disabled={isChanging}
						onClick={onMakePublic}
						role="menuitem"
						type="button">
						<ViewIcon name="globe" />
						<span className={styles.menuItemBody}>
							<span className={styles.menuItemName}>{t("view.makePublic")}</span>
							<span className={styles.menuItemDesc}>{t("view.makePublicDesc")}</span>
						</span>
					</button>
					<button
						className={styles.menuItem}
						disabled={isChanging || !canCreatePrivateTrees}
						onClick={onMakePrivate}
						role="menuitem"
						type="button">
						<ViewIcon name="lock" />
						<span className={styles.menuItemBody}>
							<span className={styles.menuItemName}>{t("view.makePrivate")}</span>
							<span className={styles.menuItemDesc}>
								{canCreatePrivateTrees ? t("view.makePrivateDesc") : t("createDialog.privateNeedsPro")}
							</span>
						</span>
					</button>
					{errorMessage && <p className={styles.error}>{errorMessage}</p>}
				</div>
			)}
		</div>
	);
};

export default PublishMenu;
